import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TableChart as TableChartIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isLocalInternalHost } from '../../utils/localInternalHost';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname.includes('ondigitalocean.app') || hostname.includes('services.knls.in')) {
      // In production, use the same origin with /api prefix
      return `${protocol}//${hostname}/api`;
    }
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

// List of tables from CRM database
const CRM_TABLES = [
  '0cart',
  '0pfclaims',
  'admin',
  'advance_generator',
  'apphistory',
  'bill_generator',
  'cart',
  'category',
  'classified_body',
  'classified_menu',
  'counter',
  'coupon',
  'coupon_assign',
  'coupon_entry',
  'c_admin',
  'employerform',
  'eshramik',
  'gds',
  'gds2',
  'gds2021',
  'hbill_generator',
  'jobseeker',
  'links',
  'multi_step_form_users',
  'mynoticeboard_body',
  'mynoticeboard_body_tst',
  'mynoticeboard_menu',
  'm_admin',
  'notes_body',
  'notes_menu',
  'notifications',
  'notification_body',
  'notification_bodyold',
  'notification_menu',
  'orders',
  'ordertrackhistory',
  'otpr',
  'panapplication',
  'pfclaims',
  'pfclaimssms',
  'pfclaimstest',
  'pmkisan',
  'prequest',
  'productreviews',
  'products',
  'shopgallery_body',
  'shopgallery_menu',
  'students',
  'subcategory',
  's_admin',
  'tblimages',
  'tblproduct',
  'ticket',
  'user',
  'usercheck',
  'userlog',
  'users',
  'usertest',
  'vtech_product',
  'vtech_user',
  'wishlist',
  'workorder',
  'w_admin',
];

const OldSite = () => {
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  
  // Search and filters
  const [search, setSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('ASC');

  // Fetch all tables
  useEffect(() => {
    fetchTables();
  }, []);

  // Fetch tables when search changes
  useEffect(() => {
    fetchTables();
  }, [tableSearch]);

  // Fetch table data when table is selected or filters change
  useEffect(() => {
    if (selectedTable) {
      fetchTableData();
    }
  }, [selectedTable, page, limit, search, sortBy, sortOrder]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to fetch from API first
      try {
        const params = tableSearch ? { search: tableSearch } : {};
        const response = await axios.get(`${API_BASE_URL}/oldsite/tables`, {
          params,
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setTables(response.data.tables || []);
          return;
        }
      } catch (apiErr) {
        console.warn('API not available, using static table list');
      }
      
      // Fallback to static list
      let filteredTables = CRM_TABLES;
      if (tableSearch) {
        filteredTables = CRM_TABLES.filter(table => 
          table.toLowerCase().includes(tableSearch.toLowerCase())
        );
      }
      setTables(filteredTables.map(tableName => ({ table_name: tableName })));
    } catch (err) {
      setError('Failed to fetch tables: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async () => {
    if (!selectedTable) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const params = {
        page,
        limit,
        search,
        sortBy,
        sortOrder,
      };

      const response = await axios.get(`${API_BASE_URL}/oldsite/tables/${selectedTable}/data`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTableData(response.data.data || []);
        setColumns(response.data.columns || []);
        setTotal(response.data.pagination?.total || response.data.data?.length || 0);
      }
    } catch (err) {
      setError('Failed to fetch table data: ' + (err.response?.data?.message || err.message));
      // If API fails, show helpful message
      if (err.response?.status === 404) {
        setError('API endpoint not found. Please ensure the backend API is configured to handle /oldsite routes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setPage(1);
    setSearch('');
    setSortBy('');
    setSortOrder('ASC');
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const filteredTables = tables.filter(table => {
    if (!tableSearch) return true;
    const tableName = typeof table === 'string' ? table : table.table_name;
    return tableName.toLowerCase().includes(tableSearch.toLowerCase());
  });

  if (!isLocalInternalHost()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Old Site Database
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and search records from the legacy CRM database
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTables}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Tables List */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Tables ({filteredTables.length})
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tables..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading && !selectedTable ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredTables.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No tables found
                  </Typography>
                ) : (
                  filteredTables.map((table) => {
                    const tableName = typeof table === 'string' ? table : table.table_name;
                    return (
                      <Box
                        key={tableName}
                        onClick={() => handleTableSelect(tableName)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          cursor: 'pointer',
                          borderRadius: 1,
                          bgcolor: selectedTable === tableName ? 'primary.light' : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          fontWeight={selectedTable === tableName ? 'bold' : 'normal'}
                        >
                          {tableName}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Table Data Viewer */}
        <Grid item xs={12} md={9}>
          {selectedTable ? (
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Table: {selectedTable}
                  </Typography>
                  
                  {/* Search and Filters */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      placeholder="Search records..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flexGrow: 1, minWidth: 200 }}
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows</InputLabel>
                      <Select
                        value={limit}
                        label="Rows"
                        onChange={(e) => { setLimit(e.target.value); setPage(1); }}
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Table */}
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              {columns.length > 0 ? (
                                columns.map((column) => (
                                  <TableCell key={column}>
                                    <TableSortLabel
                                      active={sortBy === column}
                                      direction={sortBy === column ? sortOrder.toLowerCase() : 'asc'}
                                      onClick={() => handleSort(column)}
                                    >
                                      {column}
                                    </TableSortLabel>
                                  </TableCell>
                                ))
                              ) : (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    Loading columns...
                                  </Typography>
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableData.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={columns.length || 1} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {error ? 'Error loading data' : 'No data found'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              tableData.map((row, index) => (
                                <TableRow key={index} hover>
                                  {columns.map((column) => (
                                    <TableCell key={column}>
                                      {row[column] !== null && row[column] !== undefined
                                        ? String(row[column]).substring(0, 200)
                                        : <Typography component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>NULL</Typography>}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {total > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} rows
                          </Typography>
                          <Pagination
                            count={Math.ceil(total / limit)}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <TableChartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a table from the list to view its data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {CRM_TABLES.length} tables available from the legacy CRM database
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OldSite;



